import { Done } from '@mui/icons-material'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CarrierVisitDirection,
  OrderResponseDto,
  ReleaseResponseDto,
  ReleaseType,
} from '@planning/app/api'
import { ITruckVisitItem } from '@planning/rt-stores/truckVisit/TruckVisitItem'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import { isOrderOnTerminal } from '@planning/utils/order-utils'
import {
  isAfterValidityTimestamp,
  isValidityExpiredByNow,
} from '@planning/utils/release-validity-utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { HoldEnforcementAlertWrapper } from '@planning/components/hold/HoldEnforcementAlertWrapper'
import { CustomAlert } from '../../TallymanV2/Components'

import { ContainerAlreadyAssigned } from '../Atoms/ContainerAlreadyAssigned'
import { GateInAutoComplete } from './GateInAutoComplete'
import { GeneralCargoOrderCard } from './GeneralCargoOrderCard'
import { IInspectContainerFormData } from './InspectContainer'
import { ReleaseCard } from './Molecules/ReleaseCard'
import { PickUpOrderCard } from './PickUpOrderCard'

interface OrderDto extends OrderResponseDto {
  shifts?: number
}

interface IProps {
  store: GateInViewStore
}

function autoUpsertNNROrdersWhenReady(store: GateInViewStore, releaseId: number | null) {
  if (!releaseId) return

  const nonNumericReleases = store.pickUpOrderSearchStore.releases.filter(
    or => or.type === ReleaseType.NonNumeric && or.id === releaseId,
  )

  if (nonNumericReleases.length > 0) {
    for (const nonNumericRelease of nonNumericReleases) {
      store.upsertNNROrderByNonNumericRelease(nonNumericRelease)
    }

    store.pickUpOrderSearchStore.setFilter('')
    store.pickUpOrderSearchStore.reset?.()
  }
}

export const PickUpAutoComplete: FC<IProps> = observer(({ store }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { tenantStore, truckVisitItemStore } = usePlanningStore()

  const [open, setOpen] = useState<boolean>(false)
  const pickUpSearchType = store.pickUpOrderSearchStore.searchType
  const pickUpContainerSearchType = store.pickUpOrderSearchStore.containerSearchType
  const selectedRelease = store.pickUpOrderSearchStore.releases.find(
    release => release.id === store.releaseId,
  )
  const showNumericReleaseValidityDateWarning =
    // Temporary workaround: compare full timestamp, not calendar day.
    isAfterValidityTimestamp(store.truckAppointmentDate, selectedRelease?.validityDate)

  const isNumericOrder = (c: OrderResponseDto | ReleaseResponseDto) => 'containerNumber' in c

  const isGeneralCargoOrder = (c: OrderResponseDto | ReleaseResponseDto) => {
    return 'commodityId' in c && c.commodityId !== null
  }

  const invalidPickUp = (order: OrderResponseDto | ReleaseResponseDto) => {
    if (tenantStore.skipReleaseOrder) return false

    return 'releaseEnd' in order && isValidityExpiredByNow(order.releaseEnd)
  }

  const getHoldsAlertMessage = (order: OrderResponseDto | ReleaseResponseDto) => {
    if (!order || !('holds' in order)) return <></>
    const holdCtx = store.isTruckAppointment && !store.truckVisitId ? 'TruckAppointment' : 'GateIn'
    return <HoldEnforcementAlertWrapper order={order} context={holdCtx} direction={CarrierVisitDirection.Outbound} />
  }

  const getFirstOrderFromGroupByCurrentOrder = (order: OrderResponseDto) =>
    store.pickUpOrderSearchStore.firstOrderAvailableInGroups.find(
      x =>
        x.customerGrouping === order.customerGrouping &&
        x.linkedCustomerGroupingVisitId === order.linkedCustomerGroupingVisitId,
    )

  const isNotThePriorityFromItsGroup = (order: OrderResponseDto) => {
    const firstOrder = getFirstOrderFromGroupByCurrentOrder(order)
    return (
      firstOrder &&
      store.pickUpOrderSearchStore.containerSearchType === 'containerNumber' &&
      !store.pickUpOrders.some(
        x => x.id === firstOrder?.inboundOrderId || x.id === firstOrder.outboundOrderId,
      ) &&
      firstOrder.inboundOrderId !== (order.direction === 'Inbound' ? order.id : order.linkedOrderId)
    )
  }

  const openOutboundForADifferentCarrierValidation = (
    order: OrderResponseDto | ReleaseResponseDto,
  ) => {
    if (order && 'containerNumber' in order) {
      return !!store.pickUpOrderSearchStore.orders.find(
        item =>
          item.id !== order.id &&
          item.containerNumber === order.containerNumber &&
          item.direction === CarrierVisitDirection.Outbound &&
          !!item.carrierVisitId,
      )
    }
  }

  const ReleaseExpiredAlert = () => (
    <CustomAlert
      message={t(
        'containerReleaseHasExpiredContactControlRoom',
        'Container release has expired, contact control room',
      )}
      severity='warning'
    />
  )

  const NotOnTerminalAlert = () => (
    <CustomAlert
      message={
        tenantStore.allowGateInWhenUnitNotOnTerminal
          ? t(
              'unitNotOnTerminalYetButTrucksArePermittedToProceed',
              'Unit (container or trailer) not on terminal yet, but trucks are permitted to proceed',
            )
          : t('unitNotOnTerminalYet', 'Unit not on terminal yet')
      }
      severity='warning'
    />
  )

  const OpenOutboundWithDifferentCarrierAlert = () => (
    <CustomAlert
      message={t(
        'containerHasOpenOrderForADifferentCarrier',
        'Container has an open order for a different carrier',
      )}
      severity='warning'
    />
  )

  const NumericReleaseValidityDateAlert = () => (
    <CustomAlert
      message={t(
        'truckShouldArriveBeforeValidityDate',
        'Truck should arrive before the validity date.',
      )}
      severity='warning'
    />
  )

  const getDuplicatedContainerAlertMessage = (order: OrderDto | ReleaseResponseDto) => {
    if (store.isContainerFromOrderAlreadyAssigned(order)) {
      return <ContainerAlreadyAssigned />
    }

    return <></>
  }

  const NotFirstFromGroupAlert: FC<{ containerNumber: string; orderId: number }> = ({
    containerNumber,
    orderId,
  }) => (
    <CustomAlert
      title={t('partOfSequencedGroup', 'Part of a sequenced group', {
        container: containerNumber,
      })}
      message={t('nextContainerToLeaveIs', 'Next container to leave is {container}', {
        container: containerNumber,
      })}
      severity='warning'
      action={
        <Button
          onClick={async event => {
            event.stopPropagation()
            await store.upsertPickUpOrderById(orderId)
            setOpen(false)
            store.pickUpOrderSearchStore.setFilter('')
          }}
        >
          {t('add', 'Add')}
        </Button>
      }
    />
  )

  const renderOption = (order: OrderDto | ReleaseResponseDto) => {
    if (pickUpSearchType === 'generalCargo') {
      return (
        <GeneralCargoOrderCard
          key={`order-card-${order.id}`}
          order={order as OrderResponseDto}
          validateOutboundRequest={store.validateOutboundRequest}
        />
      )
    } else if (store.isSearchingByReferenceNumber) {
      return (
        <ReleaseCard
          release={order as ReleaseResponseDto}
          highlight={store.pickUpOrderSearchStore.filter}
        />
      )
    }

    const firstOrderFromGroup = getFirstOrderFromGroupByCurrentOrder(order as OrderResponseDto)

    if (isNumericOrder(order)) {
      const orderDto = order as OrderResponseDto

      const visit: ITruckVisitItem =
        _.get(truckVisitItemStore.elements, orderDto.carrierVisitId ?? 0) ?? {}

      return (
        <PickUpOrderCard
          key={`order-card-${order.id}`}
          pickUpOrder={orderDto}
          carriers={visit?.truck ? [visit.truck.data] : []}
          informationMessage={
            (order as OrderDto).shifts
              ? `${(order as OrderDto).shifts} ${t('shiftsRequired', 'shifts required')}`
              : undefined
          }
          alertMessage={[
            getDuplicatedContainerAlertMessage(order),
            getHoldsAlertMessage(order),
            invalidPickUp(order) ? <ReleaseExpiredAlert /> : <></>,
            !isOrderOnTerminal(order) ? <NotOnTerminalAlert /> : <></>,
            openOutboundForADifferentCarrierValidation(order) ? (
              <OpenOutboundWithDifferentCarrierAlert />
            ) : (
              <></>
            ),
            showNumericReleaseValidityDateWarning ? <NumericReleaseValidityDateAlert /> : <></>,
            isNotThePriorityFromItsGroup(orderDto) ? (
              <NotFirstFromGroupAlert
                containerNumber={firstOrderFromGroup!.containerNumber!}
                orderId={
                  firstOrderFromGroup?.outboundOrderId ?? firstOrderFromGroup!.inboundOrderId!
                }
              />
            ) : (
              <></>
            ),
          ]}
        />
      )
    } else {
      return <>{`To be assigned - ${order.referenceNumber}`}</>
    }
  }

  const getOptions = () => {
    if (
      store.pickUpOrderSearchStore.searchType === 'generalCargo' ||
      store.isSearchingByReferenceNumber
    ) {
      return store.pickUpOrderSearchStore.ordersForReferenceNumber
    }

    if (
      store.pickUpOrderSearchStore.containerSearchType === 'referenceNumber' &&
      !store.releaseId
    ) {
      return store.pickUpOrderSearchStore.releases
    }

    let allPickupItems = store.pickUpOrderSearchStore.orders
      .concat(store.pickUpOrderSearchStore.releaseOrderItems)
      .filter(item => {
        return (
          !store.pickUpOrders.some(order => order.id === item.id) &&
          !store.pickUpGeneralCargoOrders.some(order => order.id === item.id)
        )
      })

    if (
      store.pickUpOrderSearchStore.containerSearchType === 'referenceNumber' &&
      store.containerShifts.length
    ) {
      allPickupItems = _(allPickupItems)
        .map(item => {
          return {
            ...item,
            shifts: store.containerShifts.find(x => x.containerNumber === item.containerNumber)
              ?.shifts,
          } as OrderDto
        })
        .sortBy(x => x.shifts)
        .value()
    }

    return allPickupItems.filter(o => !('containerNumber' in o) || !!o.containerNumber)
  }

  const addSelectedOrder = (order: OrderResponseDto | ReleaseResponseDto) => {
    if (isGeneralCargoOrder(order)) {
      store.upsertPickUpGeneralCargoOrder(order as IInspectContainerFormData)
    } else if (isNumericOrder(order)) {
      store.upsertPickUpOrder(order as IInspectContainerFormData)
    } else {
      store.upsertNNROrderByNonNumericRelease(order as ReleaseResponseDto)
    }
    store.pickUpOrderSearchStore.setFilter('')
    setOpen(false)
  }

  const handleOrderSelection = async (
    order: OrderResponseDto | ReleaseResponseDto | undefined,
  ): Promise<void> => {
    if (!order) {
      store.setReferenceNumberAndGetContainerShifts(null)
      store.setReleaseIdAndGetAdditionalInfo(null)
      return
    }

    if (isNumericOrder(order) || isGeneralCargoOrder(order)) {
      addSelectedOrder(order)
      return
    }

    const releaseDto = order as ReleaseResponseDto

    //list release orders
    if (releaseDto.type === ReleaseType.Numeric) {
      store.setReleaseIdAndGetAdditionalInfo(releaseDto.id)
    } else {
      autoUpsertNNROrdersWhenReady(store, releaseDto.id)
    }

    //TODO: get containers shifts from storage by release id

    // const isFirstStepOfReferenceSearch =
    //   store.isSearchingByReferenceNumber &&
    //   store.pickUpOrderSearchStore.filter !== (order as OrderResponseDto).releaseReference &&
    //   store.pickUpOrderSearchStore.filter !== order.referenceNumber

    // if (isFirstStepOfReferenceSearch) {
    //   const referenceValue = (order as OrderResponseDto).releaseReference ?? order.referenceNumber

    //   if (store.pickUpOrderSearchStore.filter !== referenceValue) {
    //     store.pickUpOrderSearchStore.setFilter(referenceValue!)
    //   }
    //   await store.setReferenceNumberAndGetContainerShifts(referenceValue ?? null)
    //   // autoUpsertNNROrdersWhenReady(store, referenceValue ?? null)

    //   return
    // }

    // addSelectedOrder(order)
  }

  const isOptionDisabled = (option: OrderResponseDto | ReleaseResponseDto) => {
    if (pickUpSearchType === 'generalCargo') return false

    if (isNumericOrder(option)) {
      return (
        (!store.isSearchingByReferenceNumber && invalidPickUp(option)) ||
        store.isContainerFromOrderAlreadyAssigned(option)
      )
    } else {
      const releaseOption = option as ReleaseResponseDto
      return (
        store.isSearchingByReferenceNumber &&
        (isValidityExpiredByNow(releaseOption.validityDate) ||
          (releaseOption.type === ReleaseType.Numeric &&
            releaseOption.numericReleaseOpenOrdersAmount !== undefined &&
            releaseOption.numericReleaseOpenOrdersAmount <= 0))
      )
    }
  }

  return (
    <>
      {tenantStore.hasGeneralCargo && (
        <Stack direction='row' spacing={1} mb={1}>
          <Chip
            data-cy='pick-up-autocomplete-container-chip'
            icon={pickUpSearchType === 'container' ? <Done /> : <></>}
            variant={pickUpSearchType === 'container' ? 'filled' : 'outlined'}
            label={t('unit', 'Unit')}
            onClick={() => store.pickUpOrderSearchStore.setSearchType('container')}
          />
          <Chip
            icon={pickUpSearchType === 'generalCargo' ? <Done /> : <></>}
            label={t('generalCargo', 'General cargo')}
            variant={pickUpSearchType === 'generalCargo' ? 'filled' : 'outlined'}
            onClick={() => store.pickUpOrderSearchStore.setSearchType('generalCargo')}
          />
        </Stack>
      )}

      <GateInAutoComplete
        open={open}
        setOpen={setOpen}
        dataCy='pick-up-autocomplete'
        getOptionDisabled={isOptionDisabled}
        renderOption={renderOption}
        getOptionLabel={c => {
          if (pickUpContainerSearchType === 'referenceNumber') {
            if (isNumericOrder(c)) {
              return (c as OrderResponseDto).releaseReference ?? ''
            }

            return c.referenceNumber ?? ''
          }

          if (isNumericOrder(c)) return (c as OrderResponseDto).containerNumber ?? ''

          return ''
        }}
        placeholder={
          pickUpContainerSearchType === 'containerNumber'
            ? t('searchUnitNumber', ' ')
            : t('searchReleaseReference', 'Search release reference')
        }
        store={store.pickUpOrderSearchStore}
        setContainerSearchType={store.pickUpOrderSearchStore.setContainerSearchType}
        containerSearchType={store.pickUpOrderSearchStore.containerSearchType}
        searchType={store.pickUpOrderSearchStore.searchType}
        type={'PickUp'}
        options={getOptions()}
        handleOnChange={handleOrderSelection}
        handleInputOnChange={() => {
          store.setReferenceNumberAndGetContainerShifts(null)
          store.setReleaseIdAndGetAdditionalInfo(null)
        }}
        disableCloseOnSelect={store.isSearchingByReferenceNumber}
        // ignoreResetFilter={store.pickUpOrderSearchStore.containerSearchType === 'referenceNumber'}
      />
      {store.pickUpOrderSearchStore.filter.trim() !== '' &&
        !store.pickUpOrderSearchStore.isLoading &&
        store.pickUpOrderSearchStore.isNoMatches && (
          <Box>
            <Stack
              gap={theme.customSpacing.xxs}
              direction={'row'}
              alignItems={'center'}
              sx={{
                backgroundColor: theme.palette.warning.lighter,
                color: theme.palette.warning.dark,
                padding: `${theme.customSpacing.xs}`,
                borderRadius: theme.customRadius.small,
                margin: `${theme.customSpacing.xs} 0`,
              }}
            >
              <Typography variant='caption'>
                {t(
                  'noExisitingOrderNewOrderMustBeCreated',
                  'There is no existing order for this container. A new order must be created.',
                )}
              </Typography>
            </Stack>
          </Box>
        )}
    </>
  )
})
